import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, firstValueFrom } from 'rxjs';
import { ThemeService } from '../../shared/services/theme.service';
import { ApiService } from '../../shared/services/api.service';

const STRONG_PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[^A-Za-z0-9]).{8,}$/;

function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  if (!password || !confirmPassword) return null;
  return password === confirmPassword ? null : { passwordMismatch: true };
}

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  form: FormGroup;
  loading = false;
  verifying = true;
  verifiedEmail: string | null = null;
  actionToken: string | null = null;
  invalidOrExpired = false;
  passwordSetSuccess = false;
  isDarkMode$ = this.themeService.isDarkMode$;
  private sub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private themeService: ThemeService,
    private apiService: ApiService
  ) {
    this.form = this.fb.group(
      {
        email: [{ value: '', disabled: true }],
        password: ['', [Validators.required, Validators.pattern(STRONG_PASSWORD_REGEX)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: [passwordsMatchValidator] }
    );
  }

  ngOnInit(): void {
    this.sub = this.route.queryParamMap.subscribe(async (params) => {
      this.verifying = true;
      this.invalidOrExpired = false;
      this.passwordSetSuccess = false;
      this.verifiedEmail = null;

      const token = (params.get('token') || '').trim();
      const action = params.get('action');

      if (!token || (action && action !== 'password_reset')) {
        this.invalidOrExpired = true;
        this.verifying = false;
        return;
      }

      this.actionToken = token;
      try {
        const verified = await firstValueFrom(this.apiService.verifyPasswordActionToken(token));
        if (verified.action !== 'password_reset') {
          this.invalidOrExpired = true;
          return;
        }
        this.verifiedEmail = verified.email;
        this.form.get('email')?.setValue(this.verifiedEmail);
      } catch (err: any) {
        this.invalidOrExpired = true;
      } finally {
        this.verifying = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get passwordMismatch(): boolean {
    return !!this.form.errors?.['passwordMismatch'];
  }

  async onSubmit(): Promise<void> {
    if (!this.actionToken || this.invalidOrExpired) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const password = this.form.get('password')?.value as string;

    try {
      await firstValueFrom(
        this.apiService.consumePasswordActionToken({
          token: this.actionToken,
          password,
          action: 'password_reset',
        })
      );
      this.passwordSetSuccess = true;
    } catch (err: any) {
      const reasonCode = String(err?.error?.error?.code || '');
      if (reasonCode === 'INVALID_OR_EXPIRED_TOKEN') {
        this.invalidOrExpired = true;
        this.snackBar.open('This link has expired or was already used. Please request a new one', 'Close', {
          duration: 8000
        });
      } else if (reasonCode === 'VALIDATION_ERROR') {
        this.snackBar.open(
          String(err?.error?.error?.message || 'Please check your details and try again'),
          'Close',
          { duration: 6000 }
        );
      } else {
        this.snackBar.open('Failed to update password. Please try again', 'Close', { duration: 6000 });
      }
    } finally {
      this.loading = false;
    }
  }

  async goToLogin(): Promise<void> {
    await this.router.navigate(['/login']);
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }
}

